import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FooterSection } from '../components/landing';
import { useSEO } from '../hooks/useSEO';
import { Check, ArrowRight, Loader2 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface Plan {
  id: string;
  name: string;
  display_name: string;
  description: string;
  monthly_price: string;
  yearly_price: string;
  max_users: number | null;
  boards_per_month: number | null;
  features: string[];
  is_active: boolean;
  is_recommended: boolean;
}

const faqs = [
  {
    question: 'Is there really a free plan?',
    answer: "Yes! Our Free plan is free forever with no credit card required. You get 3 boards per month with up to 5 members - perfect for trying out retrospectives.",
  },
  {
    question: 'Can I upgrade or downgrade anytime?',
    answer: "Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any charges.",
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment provider.',
  },
  {
    question: 'What happens if I exceed my board limit?',
    answer: "You can always upgrade to a higher plan to create more boards. We'll notify you when you're approaching your limit.",
  },
  {
    question: 'What happens to my data if I cancel?',
    answer: 'Your data remains available for 30 days after cancellation. You can export all your boards at any time before deletion.',
  },
  {
    question: 'Do you offer enterprise plans?',
    answer: "Yes! If you need SSO, custom integrations, or have specific requirements, contact us and we'll create a custom plan for your organization.",
  },
];

const PricingPage: React.FC = () => {
  useSEO({
    title: 'Pricing — Simple Plans for Every Team',
    description:
      'Retrosive offers transparent pricing with a generous free plan. No credit card required to start. Upgrade anytime for unlimited boards, teams, and advanced features.',
    canonical: '/pricing',
  });

  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/subscription/plans/`)
      .then((res) => {
        const data = res.data.results || res.data;
        setPlans(Array.isArray(data) ? data.filter((p: Plan) => p.is_active) : []);
      })
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const getPlanOrder = (plan: Plan): number => {
    const check = (s: string) => s.toLowerCase().trim();
    const name = check(plan.name);
    const displayName = check(plan.display_name);
    if (name === 'free' || displayName.startsWith('free')) return 0;
    if (name === 'team' || displayName.startsWith('team')) return 1;
    if (name === 'business' || displayName.startsWith('business')) return 2;
    return 99;
  };

  const sortedPlans = [...plans].sort((a, b) => getPlanOrder(a) - getPlanOrder(b));

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">

      {/* Hero */}
      <section className="pt-16 pb-10 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block px-4 py-1.5 rounded-full bg-orange-100 text-orange-700 text-sm font-semibold mb-6">
            Pricing
          </span>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 leading-tight">
            Simple, transparent pricing.
          </h1>
          <p className="mt-4 text-lg text-slate-500 max-w-xl mx-auto">
            Start free and scale as your team grows. No hidden fees, no surprises.
          </p>

          {/* Billing Toggle */}
          <div className="mt-8 inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Yearly
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="px-4 pb-20">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : sortedPlans.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-400">No plans available at the moment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-5 items-start">
              {sortedPlans.map((plan) => {
                const monthlyPrice = Number(plan.monthly_price || 0);
                const yearlyPrice = Number(plan.yearly_price || 0);
                const displayPrice =
                  billingCycle === 'yearly' && yearlyPrice > 0
                    ? yearlyPrice / 12
                    : monthlyPrice;
                const isTeamPlan = plan.is_recommended;
                const isFree = monthlyPrice === 0;
                const ctaLink = isFree ? '/register' : `/register?plan=${plan.name}`;
                const ctaLabel = isFree ? 'Get Started Free' : 'Start Free Trial';
                const planOrder = getPlanOrder(plan);
                const mobileOrder = planOrder === 0 ? 'order-first' : planOrder === 1 ? 'order-2' : 'order-last';

                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl p-7 transition-all ${mobileOrder} md:order-none ${
                      isTeamPlan
                        ? 'bg-orange-500 text-white md:scale-[1.04] z-10 shadow-xl shadow-orange-500/30'
                        : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'
                    }`}
                  >
                    {isTeamPlan && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span className="px-4 py-1.5 bg-slate-900 text-white text-xs font-bold rounded-full shadow-md flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Most Popular
                        </span>
                      </div>
                    )}

                    <h3 className={`text-xl font-bold mb-1 ${isTeamPlan ? 'text-white mt-2' : 'text-slate-900'}`}>
                      {plan.display_name}
                    </h3>
                    <p className={`text-sm mb-6 ${isTeamPlan ? 'text-orange-100' : 'text-slate-500'}`}>
                      {plan.description}
                    </p>

                    <div className="mb-6">
                      {isFree ? (
                        <>
                          <span className={`text-4xl font-black ${isTeamPlan ? 'text-white' : 'text-slate-900'}`}>Free</span>
                          <span className={`text-sm block mt-1 ${isTeamPlan ? 'text-orange-100' : 'text-slate-400'}`}>Forever</span>
                        </>
                      ) : (
                        <>
                          <span className={`text-4xl font-black ${isTeamPlan ? 'text-white' : 'text-slate-900'}`}>
                            ${displayPrice.toFixed(0)}
                          </span>
                          <span className={`text-sm ${isTeamPlan ? 'text-orange-100' : 'text-slate-400'}`}>/month</span>
                          {billingCycle === 'yearly' && yearlyPrice > 0 && (
                            <div className={`text-xs mt-1 ${isTeamPlan ? 'text-orange-100' : 'text-slate-400'}`}>
                              ${yearlyPrice}/year billed annually
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Limits */}
                    <div className={`mb-6 pb-6 border-b ${isTeamPlan ? 'border-orange-400' : 'border-slate-100'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs ${isTeamPlan ? 'text-orange-100' : 'text-slate-400'}`}>Boards:</span>
                        <span className={`text-sm font-semibold ${isTeamPlan ? 'text-white' : 'text-slate-900'}`}>
                          {plan.boards_per_month === null ? 'Unlimited' : `${plan.boards_per_month} / month`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isTeamPlan ? 'text-orange-100' : 'text-slate-400'}`}>Members:</span>
                        <span className={`text-sm font-semibold ${isTeamPlan ? 'text-white' : 'text-slate-900'}`}>
                          {plan.max_users === null ? 'Unlimited' : `Up to ${plan.max_users}`}
                        </span>
                      </div>
                    </div>

                    <Link
                      to={ctaLink}
                      className={`block w-full py-3 text-center font-semibold text-sm rounded-xl transition-colors mb-6 ${
                        isTeamPlan
                          ? 'bg-white text-orange-600 hover:bg-orange-50'
                          : 'bg-orange-500 hover:bg-orange-600 text-white'
                      }`}
                    >
                      {ctaLabel}
                    </Link>

                    {plan.features && plan.features.length > 0 && (
                      <div className="space-y-2.5">
                        {plan.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-3 text-sm">
                            <Check className={`w-4 h-4 flex-shrink-0 ${isTeamPlan ? 'text-orange-200' : 'text-orange-500'}`} />
                            <span className={isTeamPlan ? 'text-orange-50' : 'text-slate-600'}>{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Enterprise */}
          <div className="mt-10 rounded-2xl bg-orange-50 border border-orange-100 p-8 text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Enterprise</h3>
            <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
              Need SSO, custom integrations, or dedicated support? Let's create a custom plan for your organization.
            </p>
            <a
              href="mailto:admin@retrosive.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-700 font-semibold text-sm rounded-xl border border-slate-200 hover:border-orange-300 hover:text-orange-600 transition-colors"
            >
              Contact Sales
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-orange-50 px-4 py-20 border-t border-orange-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-orange-100 text-orange-700 text-sm font-semibold mb-4">
              FAQ
            </span>
            <h2 className="text-3xl font-bold text-slate-900">
              Frequently asked questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.question}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-orange-500 py-20 px-4 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to run better retrospectives?
          </h2>
          <p className="text-lg text-orange-100 mb-8">
            Start free today. No credit card required.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-600 font-semibold rounded-xl hover:bg-orange-50 transition-colors"
          >
            Get Started Free
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default PricingPage;
